import React, { useState, useEffect } from "react";
import { Plus, X, Loader2, PenLine } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { API, authHeaders } from "./shared";

export default function RequestEditForm({ request, onClose, onSave }) {
  const d = request.destination || {};
  const [form, setForm] = useState({
    items: request.items?.length ? request.items.map(it => ({ ...it })) : [{ name: "", quantity: 1, unit: "pz" }],
    destination_name: d.name || "", destination_company: d.company || "",
    destination_address: d.address || "", destination_city: d.city || "",
    destination_cap: d.cap || "", destination_province: d.province || "",
    destination_phone: d.phone || "",
    note: request.note || "",
  });
  const [saving, setSaving] = useState(false);
  const [inventory, setInventory] = useState([]);
  const locked = request.status !== "in_attesa";

  useEffect(() => {
    axios.get(`${API}/admin/warehouse/inventory?client_email=${encodeURIComponent(request.client_email || "")}`, authHeaders())
      .then((r) => setInventory(r.data?.items || r.data || []))
      .catch(() => setInventory([]));
  }, [request.client_email]);

  const invByName = Object.fromEntries(inventory.map((it) => [it.item_name, it]));
  const specsLabel = (it) => [it?.dimensions, it?.weight_kg ? `${it.weight_kg} kg` : ""].filter(Boolean).join(" · ");
  const pickInventory = (i, name) => {
    const inv = invByName[name];
    const items = [...form.items];
    items[i] = {
      ...items[i], name,
      unit: inv?.unit || items[i].unit || "pz",
      dimensions: inv?.dimensions || "", weight_kg: inv?.weight_kg ?? "", item_type: inv?.item_type || "",
      quantity: Math.min(items[i].quantity || 1, inv?.quantity || items[i].quantity || 1) || 1,
    };
    setForm({ ...form, items });
  };

  const addItem = () => setForm({ ...form, items: [...form.items, { name: "", quantity: 1, unit: "pz" }] });
  const removeItem = (i) => setForm({ ...form, items: form.items.filter((_, idx) => idx !== i) });
  const updateItem = (i, field, value) => {
    const items = [...form.items];
    items[i] = { ...items[i], [field]: value };
    setForm({ ...form, items });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.put(`${API}/admin/warehouse/requests/${request.id}`, {
        items: form.items.filter(it => it.name && it.quantity > 0),
        destination: {
          name: form.destination_name, company: form.destination_company,
          address: form.destination_address, city: form.destination_city,
          cap: form.destination_cap, province: form.destination_province,
          phone: form.destination_phone,
        },
        note: form.note,
      }, authHeaders());
      toast.success("Richiesta aggiornata");
      onClose();
      onSave();
    } catch (err) { toast.error(err.response?.data?.detail || "Errore"); }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-start justify-center pt-4 overflow-y-auto">
      <div className="bg-white rounded-sm w-full max-w-2xl shadow-xl m-4" data-testid="request-edit-form">
        <div className="flex items-center justify-between px-6 py-4 border-b border-zinc-200">
          <h2 className="font-heading font-bold text-lg uppercase tracking-wide">Modifica Richiesta — {request.request_type === "uscita" ? "Uscita Merce" : "Preparazione"}</h2>
          <button onClick={onClose} className="p-2 text-zinc-400 hover:text-zinc-600"><X size={20} /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="bg-zinc-50 border-l-2 border-zinc-400 px-3 py-2 text-xs text-zinc-700">
            Cliente: <b>{request.company || request.client_name}</b> | Stato: <b>{request.status}</b>
            <p className="text-zinc-500 mt-1">Seleziona gli articoli dalla giacenza del cliente: misure e peso registrati all'ingresso verranno riportati sulla spedizione.</p>
            {locked && <p className="text-amber-700 mt-1">⚠ Richiesta gia elaborata. Le modifiche qui non aggiorneranno l'ordine/DDT esistente.</p>}
          </div>

          <fieldset className="border border-zinc-200 rounded-sm p-4">
            <legend className="text-xs font-bold uppercase text-zinc-500 px-2">Destinatario</legend>
            <div className="grid grid-cols-2 gap-3">
              <input value={form.destination_name} onChange={e => setForm({ ...form, destination_name: e.target.value })} placeholder="Nome" className="border border-zinc-200 rounded-sm px-3 py-2 text-sm" data-testid="req-edit-dest-name" />
              <input value={form.destination_company} onChange={e => setForm({ ...form, destination_company: e.target.value })} placeholder="Azienda" className="border border-zinc-200 rounded-sm px-3 py-2 text-sm" />
              <input value={form.destination_address} onChange={e => setForm({ ...form, destination_address: e.target.value })} placeholder="Indirizzo" className="col-span-2 border border-zinc-200 rounded-sm px-3 py-2 text-sm" />
              <input value={form.destination_city} onChange={e => setForm({ ...form, destination_city: e.target.value })} placeholder="Citta" className="border border-zinc-200 rounded-sm px-3 py-2 text-sm" />
              <input value={form.destination_cap} onChange={e => setForm({ ...form, destination_cap: e.target.value })} placeholder="CAP" className="border border-zinc-200 rounded-sm px-3 py-2 text-sm" />
              <input value={form.destination_province} onChange={e => setForm({ ...form, destination_province: e.target.value })} placeholder="Prov." className="border border-zinc-200 rounded-sm px-3 py-2 text-sm" />
              <input value={form.destination_phone} onChange={e => setForm({ ...form, destination_phone: e.target.value })} placeholder="Telefono" className="border border-zinc-200 rounded-sm px-3 py-2 text-sm" />
            </div>
          </fieldset>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs font-bold uppercase text-zinc-500">Articoli</label>
              <button type="button" onClick={addItem} className="text-xs text-[#FF5200] font-bold flex items-center gap-1"><Plus size={14} /> Aggiungi</button>
            </div>
            <div className="space-y-2">
              {form.items.map((item, i) => (
                <div key={i} className="bg-zinc-50 p-2 rounded-sm" data-testid={`req-edit-item-${i}`}>
                <div className="flex items-center gap-2">
                  {inventory.length > 0 && (invByName[item.name] || !item.name) ? (
                    <select value={item.name} onChange={e => pickInventory(i, e.target.value)}
                      className="flex-1 border border-zinc-200 rounded-sm px-2 py-1.5 text-sm bg-white" data-testid={`req-edit-item-select-${i}`}>
                      <option value="">— Seleziona articolo in magazzino —</option>
                      {inventory.map((inv) => (
                        <option key={inv.item_name} value={inv.item_name}>
                          {inv.item_name} · disp. {inv.quantity} {inv.unit || "pz"}{specsLabel(inv) ? ` · ${specsLabel(inv)}` : ""}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <input value={item.name} onChange={e => updateItem(i, "name", e.target.value)} placeholder="Nome articolo"
                      className="flex-1 border border-zinc-200 rounded-sm px-2 py-1.5 text-sm" />
                  )}
                  <input type="number" min="1" max={invByName[item.name]?.quantity || undefined} value={item.quantity} onChange={e => updateItem(i, "quantity", parseInt(e.target.value) || 0)}
                    className={`w-20 border rounded-sm px-2 py-1.5 text-sm text-center ${invByName[item.name] && item.quantity > invByName[item.name].quantity ? "border-red-400 text-red-600" : "border-zinc-200"}`} />
                  <select value={item.unit || "pz"} onChange={e => updateItem(i, "unit", e.target.value)}
                    className="w-20 border border-zinc-200 rounded-sm px-2 py-1.5 text-sm">
                    <option value="pz">pz</option><option value="kg">kg</option><option value="plt">plt</option><option value="cll">cll</option><option value="box">box</option>
                  </select>
                  {form.items.length > 1 && (
                    <button type="button" onClick={() => removeItem(i)} className="p-1 text-zinc-300 hover:text-red-500"><X size={16} /></button>
                  )}
                </div>
                {(item.dimensions || item.weight_kg || invByName[item.name]) && (
                  <p className="text-[11px] text-zinc-500 mt-1 pl-1" data-testid={`req-edit-item-specs-${i}`}>
                    {invByName[item.name] ? `Disponibili: ${invByName[item.name].quantity} ${invByName[item.name].unit || "pz"}` : ""}
                    {specsLabel(item.dimensions || item.weight_kg ? item : invByName[item.name]) ? ` · Misure registrate all'ingresso: ${specsLabel(item.dimensions || item.weight_kg ? item : invByName[item.name])}` : " · Nessuna misura registrata all'ingresso"}
                  </p>
                )}
                </div>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-xs font-bold uppercase text-zinc-500 mb-1">Note</label>
            <textarea value={form.note} onChange={e => setForm({ ...form, note: e.target.value })}
              className="w-full border border-zinc-200 rounded-sm px-3 py-2 text-sm h-16 resize-none" />
          </div>

          <button type="submit" disabled={saving} data-testid="req-edit-save-btn"
            className="w-full bg-[#FF5200] text-white h-11 font-bold uppercase tracking-wide rounded-sm hover:bg-[#e64a00] transition-colors text-sm disabled:opacity-50 flex items-center justify-center gap-2">
            {saving ? <Loader2 size={16} className="animate-spin" /> : <PenLine size={16} />}
            Salva Modifiche
          </button>
        </form>
      </div>
    </div>
  );
}
